/**
 * The dead ends of a hedge maze — the cells you can only leave the way you came in.
 *
 * A perfect maze grows these for free: the backtracker in `generateMaze` carves until
 * every cell is reachable exactly one way, and the cells it leaves with a single opening
 * are its cul-de-sacs. Nothing authors them, so nothing has to keep them in step with a
 * seed change.
 *
 * This is its own module rather than a helper inside `placeTables` because two callers
 * want it for different reasons: placement wants the cells, and profile validation wants
 * only the count, to refuse a level that asks for more tables than the maze has corners
 * to stand them in. Validation must not have to import placement to ask that question.
 *
 * Pure and headless, like the rest of `src/game/`.
 */

import type { GroundPoint } from '../profile/types';
import type { Maze } from './generateMaze';

export type DeadEnd = {
  /** Cell coordinates in the maze grid. */
  readonly x: number;
  readonly z: number;
  /**
   * Unit vector from the cell towards its single opening: (±1, 0) or (0, ±1). The one
   * direction a player can arrive from — which is the direction anything placed here
   * should face.
   */
  readonly opensTo: GroundPoint;
};

/**
 * Every cell with exactly one open side, in a fixed order (north to south, west to east)
 * so that any randomness downstream is the caller's own seeded draw and not the order of
 * this list.
 *
 * The entry and exit cells are not dead ends even when three of their four sides stand:
 * the gap carved in the outer edge is their second opening, and the count below sees it,
 * because the outer edges live in the same two hedge arrays as everything else. That is
 * the right answer rather than a lucky one — a table filling the exit cell would stand
 * squarely in the gate.
 */
export function deadEnds(maze: Maze): DeadEnd[] {
  const found: DeadEnd[] = [];

  for (let z = 0; z < maze.rows; z += 1) {
    for (let x = 0; x < maze.columns; x += 1) {
      const openings: GroundPoint[] = [];
      if (!maze.verticalHedges[z]![x]) openings.push({ x: -1, z: 0 });
      if (!maze.verticalHedges[z]![x + 1]) openings.push({ x: 1, z: 0 });
      if (!maze.horizontalHedges[z]![x]) openings.push({ x: 0, z: -1 });
      if (!maze.horizontalHedges[z + 1]![x]) openings.push({ x: 0, z: 1 });

      if (openings.length === 1) found.push({ x, z, opensTo: openings[0]! });
    }
  }

  return found;
}
